import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { useGetMemberEduInfoApi, usePostModifyMemberEduInfoApi } from '@/api/mypage';
import Field from '@/components/common/Field/Field';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import AddIcon from '@/components/Icon/AddIcon';
import CloseIcon from '@/components/Icon/CloseIcon';
import { MYPAGE_RAW_COUNTRY_KOREA } from '@/constants/mypage';
import { formattedCountryOptions } from '@/constants/option';
import { useToastStore } from '@/store/toastStore';

import * as S from './style';

interface RegisterField {
	educations: { school: string; major: string; degree: string; year: string; country: string; researchField: string; studentNumber: string }[];
}

const EducationInfo = () => {
	const { t } = useTranslation();
	const { openToast } = useToastStore();
	const userRegisterSchema = z.object({
		educations: z.array(
			z.object({
				school: z.string().min(1, 'education.validation.school'),
				major: z.string().min(1, 'education.validation.major'),
				year: z.string().min(1, 'education.validation.year'),
				country: z.string().min(1, 'education.validation.country'),
				studentNumber: z.string(),
				degree: z.string(),
				researchField: z.string(),
			})
		),
	});
	const {
		control,
		watch,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			educations: [{ school: '', major: '', degree: '', year: '', country: MYPAGE_RAW_COUNTRY_KOREA, researchField: '', studentNumber: '' }],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'educations',
	});
	const { data: eduInfo } = useGetMemberEduInfoApi();
	const { mutateAsync: modifyEduInfo } = usePostModifyMemberEduInfoApi();

	const onValidateData = (_: RegisterField) => {
		append({ school: '', major: '', degree: '', year: '', country: MYPAGE_RAW_COUNTRY_KOREA, researchField: '', studentNumber: '' });
	};
	const onSubmit = async (data: RegisterField) => {
		const res = await modifyEduInfo(
			data.educations.map((item) => ({
				member_edu_school: item.school,
				member_edu_specialty: item.major,
				member_edu_degree: item.degree,
				member_edu_gradation_year: item.year,
				member_edu_nation: item.country,
				member_research_field: item.researchField,
				member_student_number: item.studentNumber,
			}))
		);

		if (res.data.success) {
			openToast({
				subContent: 'common.societyName',
				content: 'common.toast.infoUpdated',
			});
		}
	};

	useEffect(() => {
		if (eduInfo) {
			const newEducations = eduInfo.map((item) => ({
				school: item.member_edu_school,
				major: item.member_edu_specialty,
				degree: item.member_edu_degree,
				year: item.member_edu_gradation_year,
				country: item.member_edu_nation,
				researchField: item.member_research_field,
				studentNumber: item.member_student_number,
			}));

			if (newEducations.length > 0) {
				setValue('educations', newEducations);
			}
		}
	}, [eduInfo, setValue]);

	return (
		<S.FormContainer onSubmit={handleSubmit(onSubmit)}>
			<S.Container>
				{fields.map((f, index) => (
					<S.FieldBox key={f.id}>
						<S.Title>
							{t('mypage.educationInfo.itemTitle')} {index + 1 > 9 ? '' : '0'}
							{index + 1}
							{index + 1 > 1 && <CloseIcon onClose={() => remove(index)} />}
						</S.Title>
						<S.FlexRow>
							<Field title="education.fields.school" error={errors.educations?.[index]?.school?.message ?? ''}>
								<InputText
									isError={!!errors.educations?.[index]?.school?.message}
									value={watch(`educations.${index}.school`)}
									input={(e) => setValue(`educations.${index}.school`, e.target.value, { shouldValidate: true })}
									placeholder="education.placeholders.school"
								/>
							</Field>
							<Field title="education.fields.major" error={errors.educations?.[index]?.major?.message ?? ''}>
								<InputText
									isError={!!errors.educations?.[index]?.major?.message}
									value={watch(`educations.${index}.major`)}
									input={(e) => setValue(`educations.${index}.major`, e.target.value, { shouldValidate: true })}
									placeholder="education.placeholders.major"
								/>
							</Field>
						</S.FlexRow>
						<Field title="education.fields.year" error={errors.educations?.[index]?.year?.message ?? ''}>
							<S.SelectBox>
								<InputText
									isError={!!errors.educations?.[index]?.year?.message}
									value={watch(`educations.${index}.year`)}
									input={(e) => {
										e.target.value = e.target.value.replace(/[^0-9]/g, '');
										setValue(`educations.${index}.year`, e.target.value, { shouldValidate: true });
									}}
									placeholder="education.placeholders.year"
								/>
							</S.SelectBox>
						</Field>
						<Field title="education.fields.country" error={errors.educations?.[index]?.country?.message ?? ''}>
							<S.SelectBox>
								<InputSelect
									options={formattedCountryOptions}
									selectedOption={watch(`educations.${index}.country`)}
									setSelectedOption={(value) => setValue(`educations.${index}.country`, value, { shouldValidate: true })}
								/>
							</S.SelectBox>
						</Field>
					</S.FieldBox>
				))}
				<S.AddButton type="button" onClick={handleSubmit(onValidateData)}>
					<AddIcon />
					{t('common.actions.add')}
				</S.AddButton>
			</S.Container>
			<S.NextButton type="submit">{t('common.actions.completeUpdate')}</S.NextButton>
		</S.FormContainer>
	);
};

export default EducationInfo;
